import React, { useState, useCallback } from 'react';
import { Button, ListGroup, Badge, Modal, Form, Row, Col } from 'react-bootstrap';
import { useDiagramStore } from '../store/diagramStore';

const PRESET_COLORS = [
  '#fce4ec', '#e8eaf6', '#e1f5fe', '#e8f5e9',
  '#fff3e0', '#f3e5f5', '#e0f2f1', '#fff8e1',
  '#fbe9e7', '#f1f8e9', '#e3f2fd', '#ede7f6',
];

const PartnerPanel: React.FC = () => {
  const partners = useDiagramStore(s => s.partners);
  const scenarios = useDiagramStore(s => s.scenarios);
  const addPartner = useDiagramStore(s => s.addPartner);
  const removePartner = useDiagramStore(s => s.removePartner);

  const [showAdd, setShowAdd] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);

  // Count scenarios that use each partner's lane
  const scenarioCount = useCallback((partnerId: string) => {
    const laneId = `partner-${partnerId}`;
    return scenarios.filter(sc =>
      sc.diagram.lanes.some(l => l.id === laneId) ||
      sc.diagram.steps.some(st => st.laneId === laneId)
    ).length;
  }, [scenarios]);

  const handleCreate = useCallback(() => {
    if (!newName.trim()) return;
    const id = newName.trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    addPartner({ id, name: newName.trim(), color: newColor, description: newDesc.trim() || undefined } as import('../types').Partner);
    setShowAdd(false);
    setNewName('');
    setNewDesc('');
    setNewColor(PRESET_COLORS[0]);
  }, [newName, newDesc, newColor, addPartner]);

  const deletingPartner = deleteId ? partners.find(p => p.id === deleteId) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="d-flex align-items-center gap-2 px-2 py-1"
        style={{ borderBottom: '1px solid #eee', flexShrink: 0 }}>
        <small className="text-muted">{partners.length} партнёров</small>
        <div style={{ flex: 1 }} />
        <Button size="sm" variant="outline-primary"
          style={{ fontSize: '0.7rem', padding: '2px 8px' }}
          onClick={() => setShowAdd(true)}>
          + Партнёр
        </Button>
      </div>

      {/* List */}
      {partners.length === 0 ? (
        <div className="px-2 py-2 text-center text-muted" style={{ fontSize: '0.78rem' }}>
          Нет партнёров
        </div>
      ) : (
        <ListGroup variant="flush">
          {partners.map(p => {
            const count = scenarioCount(p.id);
            return (
              <ListGroup.Item key={p.id} className="py-1 px-2">
                <div className="d-flex align-items-center gap-2">
                  <div style={{
                    width: 12, height: 12, borderRadius: 2,
                    background: p.color, border: '1px solid #ccc', flexShrink: 0,
                  }} />
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="fw-semibold text-truncate" style={{ fontSize: '0.8rem' }}>
                      {p.name}
                    </div>
                    {p.description && (
                      <small className="text-muted text-truncate d-block" style={{ fontSize: '0.68rem' }}>
                        {p.description}
                      </small>
                    )}
                  </div>
                  {count > 0 && (
                    <Badge bg="secondary" style={{ fontSize: '0.6rem' }} title={`${count} сценариев`}>
                      {count}
                    </Badge>
                  )}
                  <Button size="sm" variant="outline-danger"
                    style={{ fontSize: '0.6rem', padding: '1px 4px', lineHeight: 1 }}
                    onClick={() => setDeleteId(p.id)}
                    title="Удалить">
                    &times;
                  </Button>
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}

      {/* Add Modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Новый партнёр</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-2">
            <Col xs={12}>
              <Form.Label className="small mb-1">Название *</Form.Label>
              <Form.Control size="sm" value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Например: Stripe"
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }} />
            </Col>
            <Col xs={12}>
              <Form.Label className="small mb-1">Описание</Form.Label>
              <Form.Control size="sm" value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                placeholder="Что делает партнёр..." />
            </Col>
            <Col xs={12}>
              <Form.Label className="small mb-1">Цвет зоны</Form.Label>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {PRESET_COLORS.map(c => (
                  <div key={c}
                    onClick={() => setNewColor(c)}
                    style={{
                      width: 20, height: 20, borderRadius: 3, background: c,
                      border: newColor === c ? '2px solid #333' : '1px solid #ccc',
                      cursor: 'pointer',
                    }}
                    title={c}
                  />
                ))}
              </div>
              <div className="mt-1 d-flex align-items-center gap-2">
                <div style={{ width: 20, height: 20, borderRadius: 3, background: newColor, border: '1px solid #ccc' }} />
                <Form.Control size="sm" type="color" value={newColor}
                  onChange={e => setNewColor(e.target.value)}
                  style={{ width: 36, height: 24, padding: 1, cursor: 'pointer' }} />
                <small className="text-muted">или выберите свой</small>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setShowAdd(false)}>Отмена</Button>
          <Button size="sm" variant="primary" onClick={handleCreate} disabled={!newName.trim()}>
            Добавить
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirm */}
      <Modal show={!!deleteId} onHide={() => setDeleteId(null)} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Удаление партнёра</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.85rem' }}>
          Удалить партнёра <strong>{deletingPartner?.name}</strong>?
          <br /><br />
          <small className="text-danger">
            Зона <code>partner-{deleteId}</code> исчезнет с диаграммы. Шаги сценариев, привязанных к этой зоне, станут невидимыми.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setDeleteId(null)}>Отмена</Button>
          <Button size="sm" variant="danger"
            onClick={() => { removePartner(deleteId!); setDeleteId(null); }}>
            Удалить
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PartnerPanel;
